import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { Button } from "~/components/ui/button";
import { getDBWithInit } from "~/lib/db";
import type {
  CoachSelect,
  OfferSelect,
  ProfileSelect,
  TestimonialSelect,
} from "~/../drizzle/schema";
import { CoachEmbeddingType, coachEmbeddings } from "~/../drizzle/schema";
import OpenAI from "openai";
import { cosineDistance, desc, sql } from "drizzle-orm";
import { Card } from "~/components/ui/card";
import { Textarea } from "~/components/ui/textarea";
import { serverEnv } from "~/lib/server-env";
import { MarkdownContent } from "~/components/markdown-content";
import { cn } from "~/lib/cn";
import { z } from "zod";
import type { CoachPublicData } from "~/shared/loaders";
import { createNoIndexMeta } from "~/lib/shared/meta";

// Helper to generate embeddings
async function generateEmbedding(openai: OpenAI, text: string) {
  const response = await openai.embeddings.create({
    model: "text-embedding-3-small",
    input: text,
  });
  return response.data[0].embedding;
}

// Generate coach text for each embedding type
function getCoachText(
  coach: CoachSelect & {
    profile?: ProfileSelect | null;
    mainOffer?: OfferSelect | null;
    testimonials: TestimonialSelect[];
  },
  type: CoachEmbeddingType,
) {
  switch (type) {
    case "AboutMe":
      return `${coach.profile?.openingStatement || ""} ${coach.profile?.description || ""} ${
        coach.profile?.whyContent || ""
      }`;
    case "Offer":
      return `${coach.mainOffer?.headline || ""} ${coach.mainOffer?.description || ""}`;
    case "Testimonial":
      return coach.testimonials
        .map((t: TestimonialSelect) => `${t.content} - ${t.author}`)
        .join(" ");
    default:
      return "";
  }
}

export const generateEmbeddings = createServerFn({ method: "POST" }).handler(
  async ({ context: _context }) => {
    const db = await getDBWithInit();

    const openai = new OpenAI({
      apiKey: serverEnv.OPENAI_API_KEY!,
    });

    // Get all coaches with their related data
    const coachesData = await db.query.coaches.findMany({
      with: {
        profile: true,
        mainOffer: true,
        testimonials: true,
      },
    });

    // Generate embeddings for each coach and type
    for (const coach of coachesData) {
      for (const type of [
        CoachEmbeddingType.AboutMe,
        CoachEmbeddingType.Offer,
        CoachEmbeddingType.Testimonial,
      ]) {
        const text = getCoachText(coach, type);
        if (text) {
          const embedding = await generateEmbedding(openai, text);
          await db
            .insert(coachEmbeddings)
            .values({
              coachId: coach.id,
              type,
              embedding,
            })
            .onConflictDoUpdate({
              target: [coachEmbeddings.coachId, coachEmbeddings.type],
              set: { embedding },
            });
        }
      }
    }

    return { success: true };
  },
);

const searchInputSchema = z.object({
  aboutMe: z.string().optional(),
  offer: z.string().optional(),
  testimonial: z.string().optional(),
});

type SearchInput = z.infer<typeof searchInputSchema>;

interface SearchResult {
  recommendation: string | null;
  coaches: Array<CoachPublicData & { similarity: number }>;
}

export const searchCoaches = createServerFn({ method: "POST" })
  .validator(searchInputSchema)
  .handler(async ({ data: input }): Promise<SearchResult> => {
    const openai = new OpenAI({
      apiKey: serverEnv.OPENAI_API_KEY,
    });

    const db = await getDBWithInit();
    const results: { coachId: string; totalSimilarity: number }[] = [];

    // Search for each provided input
    for (const [key, value] of Object.entries(input)) {
      if (!value) {
        continue;
      }

      const type = (key.charAt(0).toUpperCase() +
        key.slice(1)) as CoachEmbeddingType;
      const embedding = await generateEmbedding(openai, value);
      const similarity = sql<number>`1 - (${cosineDistance(coachEmbeddings.embedding, embedding)})`;

      const matches = await db
        .select({
          coachId: coachEmbeddings.coachId,
          similarity,
        })
        .from(coachEmbeddings)
        .where(sql`${coachEmbeddings.type} = ${type}`)
        .orderBy(desc(similarity))
        .limit(5);

      // Aggregate similarities
      matches.forEach((match) => {
        const existing = results.find((r) => r.coachId === match.coachId);
        if (existing) {
          existing.totalSimilarity += match.similarity;
        } else {
          results.push({
            coachId: match.coachId,
            totalSimilarity: match.similarity,
          });
        }
      });
    }

    // Get top coaches' info
    const topCoaches = await Promise.all(
      results.map(async (result) => {
        const coach = await db.query.coaches.findFirst({
          where: (coaches, { eq }) => eq(coaches.id, result.coachId),
          with: {
            profile: true,
            mainOffer: true,
            testimonials: true,
            certifications: true,
            results: true,
            resources: true,
            details: true,
          },
        });
        return {
          ...coach,
          similarity: result.totalSimilarity,
        } as CoachPublicData & { similarity: number };
      }),
    );

    // Generate LLM recommendation
    const coachesXml = topCoaches
      .map(
        (coach) => `
      <coach>
        <name>${coach.fullName}</name>
        <profileURL>https://dreamcoachmatch.com/${coach.username}</profileURL>
        <bio>
        ${coach.profile?.openingStatement || ""}
        ${coach.profile?.description || ""}
        </bio>
        <offer>
        ${coach.mainOffer?.headline || ""}
        ${coach.mainOffer?.description || ""}
        </offer>
        <testimonials>${coach.testimonials?.map((t) => t.content).join(" ")}</testimonials>
      </coach>
    `,
      )
      .join("\n");

    const prompt = `Based on the following coaches and their similarity scores to the user's request:
    ${coachesXml}
    
    User is looking for:
    About Me: ${input.aboutMe || "No preference"}
    Offer: ${input.offer || "No preference"}
    Testimonials: ${input.testimonial || "No preference"}
    
    Please recommend the best coach and explain why they would be the best fit. Format your response in markdown.
    If there's no great fit, explain why. If you find a great fit, link to their profile.
    Don't invent anything, your only options are those given to you.`;

    const completion = await openai.chat.completions.create({
      model: "google/gemini-2.0-flash-001",
      messages: [{ role: "user", content: prompt }],
    });

    return {
      recommendation: completion.choices[0].message.content,
      coaches: topCoaches,
    };
  });

function CoachFinderPage() {
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  const {
    register,
    handleSubmit,
    formState: _formState,
  } = useForm<SearchInput>();

  const generateMutation = useMutation({
    mutationFn: () => generateEmbeddings({ data: undefined }),
    onSuccess: () => {
      // Embeddings generated successfully - could add toast notification here
    },
  });

  const searchMutation = useMutation({
    mutationFn: (data: SearchInput) => searchCoaches({ data }),
    onSuccess: (result) => {
      setSearchResult(result);
    },
  });

  const onSubmit = (data: SearchInput) => {
    searchMutation.mutate(data);
  };

  return (
    <div className="container mx-auto max-w-4xl space-y-8 py-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Coach Finder</h1>
        <Button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
        >
          Generate Embeddings
        </Button>
      </div>
      <Card className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div
            className={cn("space-y-4", {
              "opacity-50": searchMutation.isPending,
              hidden: searchResult && !searchMutation.isPending,
            })}
          >
            <div>
              <label className="text-sm font-medium">
                What background and experience are you looking for in a coach?
              </label>
              <Textarea {...register("aboutMe")} />
            </div>

            <div>
              <label className="text-sm font-medium">
                What type of coaching package or offer interests you?
              </label>
              <Textarea {...register("offer")} />
            </div>

            <div>
              <label className="text-sm font-medium">
                What kind of results or testimonials are you looking for?
              </label>
              <Textarea {...register("testimonial")} />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={searchMutation.isPending}
          >
            Find My Perfect Coach
          </Button>
        </form>
      </Card>
      {searchResult && (
        <Card className="prose dark:prose-invert max-w-none p-6">
          <MarkdownContent
            content={searchResult.recommendation ?? "No result."}
          />
        </Card>
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/finder/")({
  head: () =>
    createNoIndexMeta(
      "Admin - Coach Finder – Dream Coach Match",
      "Admin tool for finding and searching coaches using AI-powered semantic search",
    ),
  component: CoachFinderPage,
});
